/**
 * Lettura e scrittura del formato `.env`.
 *
 * Il formato non ha una specifica ufficiale: questa implementazione segue il
 * comportamento di `docker compose`, che è quello che gli utenti si aspettano
 * visto che è lo stesso file che finirà a Compose.
 */

export type EnvMap = Record<string, string>;

/**
 * Nome del file che porta l'environment dentro i container.
 *
 * Un servizio che lo dichiara in `env_file:` riceve tutte le variabili dello
 * stack; Portman lo materializza nella cartella di lavoro prima di ogni comando
 * compose. È lo stesso nome usato da Portainer, perché chi arriva da lì trovi
 * il compose che già conosce — con una differenza che conta: qui il file
 * elenca soltanto i nomi, e i valori restano cifrati nel database.
 */
export const STACK_ENV_FILE = "stack.env";

/** Parti di nome che indicano un valore da non mostrare né registrare in chiaro. */
const SENSITIVE_HINTS = [
  // "pass" copre `DB_PASS`, `SMTP_PASS`, `PASSPHRASE`, oltre a password e
  // passwd. Prende anche `BYPASS` o `COMPASS`: mascherare un valore innocuo
  // costa un clic su «rivela», lasciare in chiaro una password no.
  "pass",
  "jwt",
  "salt",
  "signing",
  "secret",
  "token",
  "apikey",
  "api_key",
  "private_key",
  // Copre `MASTER_KEY`, `ENCRYPTION_KEY`, `SIGNING_KEY`: chiavi che l'euristica
  // lasciava passare in chiaro perché nominate senza "secret" né "password".
  // Con l'underscore, e non "key" nudo, `KEYCLOAK_*` e simili restano fuori.
  "_key",
  "credential",
  "dsn",
  "auth",
];

/**
 * Indizi che valgono solo come parola intera del nome.
 *
 * `GITHUB_PAT` è un token, ma "pat" come sottostringa prenderebbe ogni `_PATH`.
 */
const SENSITIVE_WORDS = ["pat"];

/**
 * Credenziali dentro un URL: `postgres://utente:password@db`.
 *
 * Il nome qui non aiuta — `DATABASE_URL` finisce in `_url` proprio come
 * `NEXTAUTH_URL` — quindi decide il valore. Serve una password non vuota dopo
 * i due punti: `utente@host` da solo non è un segreto.
 */
const URL_CON_CREDENZIALI = /^[a-z][a-z0-9+.-]*:\/\/[^\s/:@]*:[^\s/@]+@/i;

/**
 * Suffissi che smentiscono il sospetto.
 *
 * "auth" nel nome è un indizio utile — `REDIS_AUTH` è davvero una password — ma
 * da solo produce falsi positivi: `NEXTAUTH_URL` e `OCI_AUTH_TYPE` non sono
 * segreti, e mascherarli nasconde informazioni che servono a leggere la
 * configurazione senza proteggere nulla.
 */
const NOT_SENSITIVE_SUFFIXES = [
  "_url",
  "_uri",
  "_type",
  "_mode",
  "_enabled",
  "_host",
  "_hostname",
  "_port",
  "_user",
  "_username",
  "_region",
  "_bucket",
  "_endpoint",
  "_provider",
  "_issuer",
  "_expiration",
  "_ttl",
];

/**
 * Ciò che l'utente vede al posto di un segreto.
 *
 * È esportata perché non è solo una decorazione: quando torna indietro in un
 * salvataggio significa «lascia il valore che c'è», e chi la riceve deve poterla
 * riconoscere invece di cifrarla al posto della password.
 */
export const MASK = "••••••••";

/**
 * Nome di variabile ammesso.
 *
 * Lo stesso che il parser sa rileggere: una chiave con spazi, `=` o a capo
 * scritta in `stack.env` produrrebbe righe che cambiano significato alla
 * rilettura, o variabili inventate da chi ha scelto il nome.
 */
export const ENV_KEY = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** Riconosce `[export ]CHIAVE = valore`. */
const ASSIGNMENT = /^\s*(?:export\s+)?([A-Za-z_][A-Za-z0-9_]*)\s*=(.*)$/;

/**
 * Interpreta il valore a destra dell'uguale.
 *
 * Le regole di quoting non sono decorative: fra virgolette doppie le sequenze
 * di escape valgono e il cancelletto è un carattere normale, mentre in un
 * valore non quotato il cancelletto apre un commento.
 */
function parseValue(raw: string): string {
  const trimmed = raw.trim();

  if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length >= 2) {
    return trimmed
      .slice(1, -1)
      .replaceAll("\\n", "\n")
      .replaceAll("\\r", "\r")
      .replaceAll("\\t", "\t")
      .replaceAll('\\"', '"')
      .replaceAll("\\\\", "\\");
  }

  if (trimmed.startsWith("'") && trimmed.endsWith("'") && trimmed.length >= 2) {
    return trimmed.slice(1, -1);
  }

  const commentAt = trimmed.indexOf(" #");

  return (commentAt === -1 ? trimmed : trimmed.slice(0, commentAt)).trim();
}

export function parseEnvFile(text: string): EnvMap {
  const env: EnvMap = {};

  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed.length === 0 || trimmed.startsWith("#")) continue;

    const match = ASSIGNMENT.exec(line);
    if (!match) continue;

    const [, key = "", value = ""] = match;
    env[key] = parseValue(value);
  }

  return env;
}

/** Serve quotare il valore per poterlo rileggere senza ambiguità? */
const needsQuoting = (value: string) =>
  value !== value.trim() || /[\s#"'\\]/.test(value) || value.includes("\n");

const quote = (value: string) =>
  `"${value
    .replaceAll("\\", "\\\\")
    .replaceAll('"', '\\"')
    .replaceAll("\n", "\\n")
    .replaceAll("\r", "\\r")
    .replaceAll("\t", "\\t")}"`;

/**
 * Scrive una mappa in formato `.env`, con le chiavi in ordine alfabetico.
 *
 * L'ordine stabile è ciò che rende leggibile il diff fra due revisioni: senza,
 * ogni salvataggio sembrerebbe aver cambiato tutto.
 */
export function serializeEnvFile(env: EnvMap): string {
  return Object.keys(env)
    .sort()
    .map((key) => {
      const value = env[key] ?? "";
      return `${key}=${needsQuoting(value) ? quote(value) : value}`;
    })
    .join("\n");
}

/** Il nome della variabile suggerisce che il valore sia un segreto? */
export function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase();

  const parole = normalized.split("_");
  const indiziato =
    SENSITIVE_HINTS.some((hint) => normalized.includes(hint)) ||
    SENSITIVE_WORDS.some((parola) => parole.includes(parola));

  if (!indiziato) return false;

  // Un indizio esplicito ha la meglio sul suffisso: `SECRET_ACCESS_KEY_URL`
  // resterebbe comunque un segreto.
  const esplicito = [
    "password",
    "passwd",
    "passphrase",
    "secret",
    "private_key",
    "credential",
  ].some((hint) => normalized.includes(hint));

  return esplicito || !NOT_SENSITIVE_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}

/**
 * Il valore va nascosto? Lo dice il nome, oppure il valore stesso quando porta
 * credenziali in un URL.
 */
export function isSensitiveEntry(key: string, value: string): boolean {
  return isSensitiveKey(key) || URL_CON_CREDENZIALI.test(value);
}

/**
 * Sostituisce i valori sensibili con una maschera.
 *
 * Un valore vuoto resta vuoto: distinguere "non impostata" da "impostata e
 * nascosta" è proprio ciò che serve sapere guardando la configurazione.
 */
export function maskSensitive(env: EnvMap): EnvMap {
  return Object.fromEntries(
    Object.entries(env).map(([key, value]) => [
      key,
      isSensitiveEntry(key, value) && value !== "" ? MASK : value,
    ]),
  );
}

/**
 * Sostituisce le maschere tornate indietro con i valori che nascondevano.
 *
 * Chi apre uno stack riceve i segreti mascherati, e se poi salva avendo
 * modificato soltanto il compose li rimanda esattamente come li ha ricevuti.
 * Senza questo passaggio la maschera verrebbe cifrata al posto della password,
 * in silenzio: il salvataggio riesce, e il guasto arriva molto più tardi, da un
 * servizio che rifiuta le credenziali senza che nulla lo colleghi a quella
 * modifica.
 *
 * Vale solo per le chiavi che Portman maschera: altrove quella stringa non
 * l'ha scritta lui, e reinterpretarla sarebbe arbitrario.
 *
 * Un limite resta, e va detto invece di lasciarlo scoprire: un segreto il cui
 * valore è davvero la maschera non si può impostare, perché quella stringa
 * significa già «non l'ho toccato» e non può anche significare sé stessa.
 * Rifiutarla non sarebbe una via d'uscita — arriva a ogni salvataggio
 * ordinario, e rifiutarla li respingerebbe tutti.
 */
export function conservaMascherati(precedente: EnvMap, nuovo: EnvMap): EnvMap {
  return Object.fromEntries(
    Object.entries(nuovo).map(([chiave, valore]) => {
      // Si guarda il valore precedente, non quello arrivato: un URL con
      // credenziali è stato mascherato per ciò che conteneva, non per il nome.
      const daConservare =
        valore === MASK &&
        precedente[chiave] !== undefined &&
        isSensitiveEntry(chiave, precedente[chiave]);

      return [chiave, daConservare ? precedente[chiave]! : valore];
    }),
  );
}
